import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './PublicAgent.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.libbenagents.com';
const APP_URL = process.env.REACT_APP_APP_URL || 'https://app.libbenagents.com';

export default function PublicAgent() {
  const { userSlug, agentSlug } = useParams();
  const { t, i18n }             = useTranslation();
  const [agent, setAgent]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [msgs, setMsgs]         = useState([]);
  const [input, setInput]       = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId]             = useState(() => `pub_${Math.random().toString(36).slice(2, 14)}`);
  const msgsRef                 = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/public/agent-page/${userSlug}/${agentSlug}`)
      .then(r => r.json())
      .then(d => {
        setAgent(d);
        setMsgs([{ role: 'agent', text: d.welcomeMessage || t('publicAgent.welcome', { name: d.name }) }]);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [userSlug, agentSlug]); // eslint-disable-line

  useEffect(() => {
    if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
  }, [msgs]);

  const send = async () => {
    const text = input.trim();
    if (!text || chatLoading || !agent) return;
    setInput('');
    setMsgs(m => [...m, { role: 'user', text }]);
    setChatLoading(true);
    try {
      await fetch(`${API_URL}/internal-chat/${agent.tenantId}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, message: text, agentId: agent.instanceId })
      });
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        const r = await fetch(`${API_URL}/internal-chat/${agent.tenantId}/poll?sessionId=${sessionId}`);
        const data = await r.json();
        if (data.response) {
          clearInterval(poll);
          setMsgs(m => [...m, { role: 'agent', text: data.response }]);
          setChatLoading(false);
        }
        if (attempts > 20) { clearInterval(poll); setChatLoading(false); }
      }, 1500);
    } catch {
      setMsgs(m => [...m, { role: 'agent', text: t('publicAgent.errorMsg') }]);
      setChatLoading(false);
    }
  };

  if (loading) return <div className="pa-loading">{t('publicAgent.loading')}</div>;
  if (!agent)  return <div className="pa-loading">{t('publicAgent.notFound')}</div>;

  return (
    <>
      <Helmet>
        <title>{agent.name} | Libben AI</title>
        <meta name="description" content={agent.description || t('publicAgent.metaDesc', { name: agent.name, role: agent.role, defaultValue: `Converse com ${agent.name}, ${agent.role}` })} />
        <meta property="og:title" content={agent.name} />
        <html lang={i18n.language} />
      </Helmet>

      <div className="pa-page" style={{ '--pa-color': agent.primaryColor || '#6366f1' }}>
        <div className="pa-header">
          <div className="pa-avatar">{agent.icon || '🤖'}</div>
          <div>
            <h1 className="pa-name">{agent.name}</h1>
            <p className="pa-role">{agent.role}</p>
          </div>
        </div>

        <div className="pa-chat">
          <div className="pa-msgs" ref={msgsRef}>
            {msgs.map((m, i) => (
              <div key={i} className={`pa-msg pa-msg-${m.role}`}>{m.text}</div>
            ))}
            {chatLoading && (
              <div className="pa-msg pa-msg-agent">
                <span className="pa-typing"><span/><span/><span/></span>
              </div>
            )}
          </div>
          <div className="pa-input-row">
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && send()}
              placeholder={t('publicAgent.placeholder')}
              disabled={chatLoading}
            />
            <button onClick={send} disabled={chatLoading || !input.trim()}>→</button>
          </div>
        </div>

        <div className="pa-footer">
          {t('publicAgent.poweredBy')}{' '}
          <a href={`${APP_URL}/register?utm=public_agent`} target="_blank" rel="noopener noreferrer">Libben AI</a>
        </div>
      </div>
    </>
  );
}
